import {useEffect, useState, useRef} from 'react';
import * as THREE from 'three';
import {GLTFLoader} from 'three/examples/jsm/loaders/GLTFLoader';
import {DRACOLoader} from 'three/examples/jsm/loaders/DRACOLoader';

export interface SubTypeModel {
  model: THREE.Object3D;
  scale: number;
}

export interface AssetModels {
  equipment: Map<string, SubTypeModel>;
  personnel: SubTypeModel | null;
}

// Map equipment subtypes to their GLB model files
// Paths adjusted from webview-relative to web/public absolute URLs
const EQUIPMENT_MODEL_PATHS: Record<string, string> = {
  truck: '/samples/equipment/hauler.glb',
  rescue_vehicle: '/samples/equipment/hauler.glb',
  loader: '/samples/equipment/loader.glb',
  drill_rig: '/samples/equipment/driller.glb',
};

const PERSONNEL_MODEL_PATH = '/samples/personnel/miner.glb';

// Target sizes in world units
const TARGET_SIZES = {
  equipment: 60,
  personnel: 25,
};

function computeNormalizeScale(object: THREE.Object3D, targetSize: number): number {
  const box = new THREE.Box3().setFromObject(object);
  const size = new THREE.Vector3();
  box.getSize(size);
  const maxDim = Math.max(size.x, size.y, size.z);
  if (maxDim === 0) return 1;
  return targetSize / maxDim;
}

/**
 * Apply KHR_materials_pbrSpecularGlossiness diffuse colors to loaded model.
 * Three.js 0.160+ dropped support for this extension, so GLTFLoader produces
 * default white MeshStandardMaterial. We read the GLTF JSON to recover the
 * original diffuseFactor colors and convert materials to unlit MeshBasicMaterial.
 */
function applySpecularGlossinessMaterials(gltf: any): void {
  const json = gltf.parser?.json;
  if (!json?.materials) return;

  // Build material index → MeshBasicMaterial with correct diffuse color
  const fixedMaterials: THREE.MeshBasicMaterial[] = json.materials.map((mat: any) => {
    const ext = mat.extensions?.KHR_materials_pbrSpecularGlossiness;
    let color = new THREE.Color(0xffffff);
    if (ext?.diffuseFactor) {
      // diffuseFactor is linear RGB — Three.js Color.setRGB expects linear by default
      color = new THREE.Color(
        ext.diffuseFactor[0],
        ext.diffuseFactor[1],
        ext.diffuseFactor[2],
      );
    }
    const basicMat = new THREE.MeshBasicMaterial({
      color,
      side: mat.doubleSided ? THREE.DoubleSide : THREE.FrontSide,
    });
    // MeshBasicMaterial is unlit — bypasses scene lighting + tone mapping issues
    basicMat.toneMapped = false;
    return basicMat;
  });

  // Replace materials on each mesh using GLTF mesh→primitive→material index mapping
  const meshDefs = json.meshes || [];
  gltf.scene.traverse((child: any) => {
    if (!child.isMesh) return;

    // Try to get material index from parser associations first
    const assoc = gltf.parser?.associations?.get(child);
    if (assoc && assoc.meshes !== undefined && assoc.primitives !== undefined) {
      const meshDef = meshDefs[assoc.meshes];
      if (meshDef?.primitives) {
        const primDef = meshDef.primitives[assoc.primitives];
        if (primDef?.material !== undefined && fixedMaterials[primDef.material]) {
          child.material = fixedMaterials[primDef.material];
          return;
        }
      }
    }

    // Fallback: match by traversal order within each GLTF mesh
    // Each GLTF mesh can have multiple primitives, each becoming a Three.js Mesh
    if (Array.isArray(child.material)) {
      child.material = child.material.map((_: any, i: number) => {
        return fixedMaterials[i] || fixedMaterials[0];
      });
    }
  });
}

export function useAssetModels(): AssetModels {
  const [models, setModels] = useState<AssetModels>({
    equipment: new Map(),
    personnel: null,
  });
  const loaderRef = useRef<GLTFLoader | null>(null);

  useEffect(() => {
    const loader = new GLTFLoader();
    const dracoLoader = new DRACOLoader();
    dracoLoader.setDecoderPath(
      'https://www.gstatic.com/draco/versioned/decoders/1.5.6/',
    );
    loader.setDRACOLoader(dracoLoader);
    loaderRef.current = loader;

    // Load each equipment subtype model
    const loadedEquipment = new Map<string, SubTypeModel>();
    // Track unique paths to avoid loading the same file twice
    const pathToSubTypes = new Map<string, string[]>();
    for (const [subType, path] of Object.entries(EQUIPMENT_MODEL_PATHS)) {
      if (!pathToSubTypes.has(path)) {
        pathToSubTypes.set(path, []);
      }
      pathToSubTypes.get(path)!.push(subType);
    }

    let equipmentLoadCount = 0;
    const totalEquipmentLoads = pathToSubTypes.size;

    for (const [path, subTypes] of pathToSubTypes.entries()) {
      loader.load(
        path,
        gltf => {
          applySpecularGlossinessMaterials(gltf);
          const model = gltf.scene;
          const scale = computeNormalizeScale(model, TARGET_SIZES.equipment);
          for (const subType of subTypes) {
            loadedEquipment.set(subType, {model: subType === subTypes[0] ? model : model.clone(true), scale});
          }
          equipmentLoadCount++;
          if (equipmentLoadCount >= totalEquipmentLoads) {
            setModels(prev => ({...prev, equipment: new Map(loadedEquipment)}));
          }
        },
        undefined,
        err => {
          console.warn(`[AssetModels] Failed to load equipment model ${path}:`, err);
          equipmentLoadCount++;
        },
      );
    }

    // Load personnel model
    loader.load(
      PERSONNEL_MODEL_PATH,
      gltf => {
        applySpecularGlossinessMaterials(gltf);
        const model = gltf.scene;
        const scale = computeNormalizeScale(model, TARGET_SIZES.personnel);
        setModels(prev => ({...prev, personnel: {model, scale}}));
      },
      undefined,
      err => {
        console.warn('[AssetModels] Failed to load personnel model:', err);
      },
    );

    return () => {
      dracoLoader.dispose();
    };
  }, []);

  return models;
}
